import React, { useEffect, useState } from 'react';
import './App.css';
import { ConfigProvider, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from './features/auth/authSlice';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { Login, Register, Development, Design, Settings, Dashboard, Certifications, Profile, Media, Blog, Account, Documentation, Privacy } from './pages/index';
import { AppLayout } from './layouts/index';
import '../src/tailwind.css';

const ROLES = {
  'Root': 'root',
  'Admin': 'admin', 
  'DevUser': 'devuser',
  'DesUser': 'desuser',
  'ProUser': 'prouser',
  'Visitor': 'visitor'
};

function App() {
  const dispatch = useDispatch();
  const [initialLoad, setInitialLoad] = useState(true);
     
  useEffect(() => {
  const checkAuth = async () => {
    try {
      await dispatch(login()).unwrap();
      // Auth successful, stop loading immediately
      setInitialLoad(false);
    } catch (error) {
      // Auth failed (this is expected for users without tokens)
      // Clear the error so it doesn't show on login/register pages
      dispatch(clearError());
      setInitialLoad(false);
    }
  };
       
  checkAuth();
       
  // Fallback timeout - if auth takes too long, stop loading anyway
  const fallbackTimer = setTimeout(() => {
    setInitialLoad(false);
  }, 800); // Shorter fallback - 800ms
       
  return () => clearTimeout(fallbackTimer);
}, [dispatch]);
     
  const user = useSelector((state) => state.auth.user);
     
  // Show loading only until auth completes or timeout
  if (initialLoad) {
    return (
      <ConfigProvider theme={{ token: { colorPrimary: 'hsl(185,69%,54%)' } }}>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Spin size="large" />
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </ConfigProvider>
    );
  }

  // Helper function to check if user has access to a route
  const hasAccess = (allowedRoles) => {
    return allowedRoles.includes(user?.role);
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: 'hsl(185,69%,54%)' } }}>
      <BrowserRouter>
        <Routes>
          <Route path='/login' element={user ? <Navigate to='/' /> : <Login />} />
          <Route path='/register' element={<Register />} />
          
          {/* Privacy - Public route with proper wrapper */}
          <Route path='/privacy' element={
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
              <div className="max-w-6xl mx-auto">
                <Privacy />
              </div>
            </div>
          } />
          
          <Route path='/' element={user ? <AppLayout /> : <Navigate to='/login' />}>
            <Route index element={<Dashboard />} />
            
            {/* Development - DevUser, ProUser, Admin, Visitor */}
            <Route 
              path='/development' 
              element={
                hasAccess([ROLES.DevUser, ROLES.ProUser, ROLES.Admin, ROLES.Visitor]) 
                  ? <Development /> 
                  : <Navigate to='/' />
              } 
            />
            
            {/* Design - DesUser, ProUser, Admin, Visitor */}
            <Route 
              path='/design' 
              element={
                hasAccess([ROLES.DesUser, ROLES.ProUser, ROLES.Admin, ROLES.Visitor]) 
                  ? <Design /> 
                  : <Navigate to='/' />
              } 
            />
            {/* Settings - DesUser, ProUser, Admin, Visitor */}
            <Route 
              path='/settings' 
              element={
                hasAccess([ROLES.DesUser,ROLES.DevUser, ROLES.ProUser, ROLES.Admin, ROLES.Visitor]) 
                  ? <Settings /> 
                  : <Navigate to='/' />
              } 
            />
            
            {/* Profile - DesUser, ProUser, Admin, Visitor */}
            <Route 
              path='/profile' 
              element={
                hasAccess([ROLES.DevUser,ROLES.DesUser, ROLES.ProUser, ROLES.Admin, ROLES.Visitor]) 
                  ? <Profile /> 
                  : <Navigate to='/' />
              } 
            />
            
            {/* Certifications - All authenticated users */}
            <Route path='/certifications' element={<Certifications />} />
            
            {/* Blog - ProUser, Admin, Visitor only */}
            <Route 
              path='/blog' 
              element={
                hasAccess([ROLES.ProUser, ROLES.Admin, ROLES.Visitor]) 
                  ? <Blog /> 
                  : <Navigate to='/' />
              } 
            />
            
            {/* Documentation - All authenticated users */}
            <Route path='/documentation' element={<Documentation />} />
            
            {/* Media - DevUser, DesUser, ProUser, Admin, Visitor */}
            <Route 
              path='/media' 
              element={
                hasAccess([ROLES.DevUser, ROLES.DesUser, ROLES.ProUser, ROLES.Admin, ROLES.Visitor]) 
                  ? <Media /> 
                  : <Navigate to='/' />
              } 
            />
            
            {/* Account - Root only */}
            <Route 
              path='/account' 
              element={
                hasAccess([ROLES.Root]) 
                  ? <Account /> 
                  : <Navigate to='/' />
              } 
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;